// src/auth/useAuthState.js
import { useReducer, useEffect, useRef } from 'react';
import { API_V1, getSessionHeaders } from '../services/api/config.js';
import { PURGED_TENANTS_KEY, loadPurgedTenants, withoutPurged, dropPurgedTenant, isTenantPurgedResponse } from './tenantPurged.js';

// ── localStorage helpers ─────────────────────────────────────────────────
export function saveAuth({ token, tenants, customers }) {
  localStorage.setItem('auth_token', token);
  localStorage.setItem('auth_tenants', JSON.stringify(tenants));
  // `customers` is optional: an old backend omits it from LoginResponse entirely.
  // Persist `[]` in that case so the restored state is the safe default (no rights).
  localStorage.setItem('auth_customers', JSON.stringify(customers || []));
  // A new session's claims are built from the DB, where a purged workspace no longer exists (tenantPurged.js).
  localStorage.removeItem(PURGED_TENANTS_KEY);
}

export function saveTenant(tenantId) {
  localStorage.setItem('auth_tenant_id', tenantId);
}

export function saveTenants(tenants) {
  localStorage.setItem('auth_tenants', JSON.stringify(tenants));
}

export function saveCustomers(customers) {
  localStorage.setItem('auth_customers', JSON.stringify(customers || []));
}

export function clearAuthStorage() {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('auth_tenants');
  localStorage.removeItem('auth_customers');
  localStorage.removeItem('auth_tenant_id');
  localStorage.removeItem('auth_admin_mode');
  localStorage.removeItem(PURGED_TENANTS_KEY);
}

export function loadAuthStorage() {
  const token = localStorage.getItem('auth_token');
  const tenants = JSON.parse(localStorage.getItem('auth_tenants') || 'null');
  const customers = JSON.parse(localStorage.getItem('auth_customers') || 'null');
  const tenantId = localStorage.getItem('auth_tenant_id');
  return { token, tenants, customers, tenantId };
}

// ── Reducer ──────────────────────────────────────────────────────────────
const initialState = {
  phase: 'booting', // 'booting' | 'login' | 'no_tenants' | 'tenant_pick' | 'ready'
  user: null,       // { user_id, email, is_active, first_name, last_name, locale, last_login_at }
  token: null,
  tenants: [],      // [{ tenant_id, role, display_name }]
  // ⚠️ CLAIM-BOUND, for the whole life of the token. `customers` is minted into
  // the JWT at POST /auth/login, and GET /auth/me merely REPLAYS those claims
  // (the BE builds MeResponse from a users row + the token's own tenant/customer
  // claims) — so /me can never observe a promotion or a revocation here.
  // ⛔ The backend serves NO claims-refresh route: a promotion or a revocation
  // becomes visible only at the next sign-in (JWT_EXPIRY_MINUTES, default 24h,
  // bounds the wait). The one exception is an invitation accept, whose 200 body
  // is a whole new session (AuthContext.applySession). The server-side 403
  // remains the real gate, and the permissions-drift notice is how the user is
  // told to sign in again.
  // ⛔ Do NOT "fix" anything here by refetching /auth/me: it reads the same token
  // and would report the stale state back as if it were fresh.
  customers: [],    // [{ customer_id, role, legal_name }] — role ∈ 'admin' | 'viewer'
  activeTenant: null,
  applications: [], // [{ id, display_name, description, sort_order }]
  isSuperadmin: false,
  adminMode: false,
  error: null,
};

export function authReducer(state, action) {
  switch (action.type) {
    case 'BOOT_DONE': {
      const adminMode = !!action.adminMode;
      localStorage.setItem('auth_admin_mode', adminMode ? '1' : '');
      return {
        ...state,
        phase: action.phase,
        user: action.user,
        token: action.token,
        tenants: action.tenants,
        customers: action.customers || [],
        activeTenant: action.activeTenant,
        applications: action.applications,
        isSuperadmin: action.isSuperadmin ?? false,
        adminMode,
      };
    }
    case 'SET_USER':
      return { ...state, user: action.user };
    case 'SET_SUPERADMIN':
      return { ...state, isSuperadmin: action.value };
    case 'SET_ADMIN_MODE': {
      localStorage.setItem('auth_admin_mode', action.value ? '1' : '');
      // Hybrid-SA flow: entering admin mode while on the tenant picker must advance
      // phase past 'tenant_pick', otherwise AppShell bounces /admin/* back to /select-tenant.
      const phase = action.value && state.phase === 'tenant_pick' ? 'ready' : state.phase;
      return { ...state, adminMode: action.value, phase };
    }
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        phase: action.phase,
        user: action.user,
        token: action.token,
        tenants: action.tenants,
        customers: action.customers || [], // absent on an old backend → no rights
        error: null,
      };
    case 'TENANT_SELECTED':
      return { ...state, phase: 'ready', activeTenant: action.activeTenant, applications: action.applications };
    case 'LOGOUT':
      return { ...initialState, phase: 'login' };
    case 'SET_ERROR':
      return { ...state, error: action.error };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    case 'SESSION_EXPIRED':
      return { ...initialState, phase: 'login' };
    case 'TENANT_DENIED':
      return { ...state, activeTenant: null, phase: 'tenant_pick' };
    case 'TENANT_PURGED': {
      // 410 TENANT_PURGED (auth/tenantPurged.js): the workspace is gone for everyone, for ever. Unlike
      // TENANTS_REFRESHED this is not a new session — it removes ONE workspace and touches nothing else.
      // A NON-active workspace (the Gestione Workspace «Utenti» selector, a superadmin's ?tenant= page) only
      // leaves the list; the navigation, if any, is AuthContext's. In admin mode the phase never moves: a
      // `tenant_pick` phase would make AppShell bounce /admin/* to the picker.
      const tenants = (state.tenants || []).filter(t => t.tenant_id !== action.tenantId);
      const wasActive = !!action.wasActive || state.activeTenant?.tenant_id === action.tenantId;
      if (!wasActive) {
        return tenants.length === (state.tenants || []).length ? state : { ...state, tenants };
      }
      if (state.adminMode) {
        return { ...state, tenants, activeTenant: null, applications: [] };
      }
      let phase = 'no_tenants';
      if (tenants.length > 0) phase = 'tenant_pick';
      else if (state.isSuperadmin) phase = 'ready';
      return { ...state, tenants, activeTenant: null, applications: [], phase };
    }
    case 'TENANTS_REFRESHED': {
      // The caller is AuthContext.applyClaims, and it only ever runs on a WHOLE
      // NEW SESSION: a /login response, or the re-minted session an invitation
      // accept hands back (applySession). There is no claims-refresh route, so a
      // role change or a removal made mid-session does NOT arrive here — it waits
      // for the next sign-in.
      // ⛔ It must never be fed from /auth/me: that payload's `tenants` is the
      // caller's own JWT claim replayed, so it would compare the token against
      // itself. (`is_superadmin` / `is_active` / profile on /me ARE DB-fresh —
      // only its two claim arrays are not.)
      // The server-side 403 remains the authoritative gate: a TENANT_ACCESS_DENIED
      // drives TENANT_DENIED → the tenant picker, whatever this list says.
      const newTenants = action.tenants || [];
      if (!state.activeTenant) {
        return { ...state, tenants: newTenants };
      }
      const stillThere = newTenants.find(t => t.tenant_id === state.activeTenant.tenant_id);
      if (stillThere) {
        return { ...state, tenants: newTenants, activeTenant: stillThere };
      }
      // Active tenant was removed — navigation side-effect handled in AuthContext.
      // Reachable only on a new session (a sign-in, or an invitation accept whose
      // fresh claims no longer list the workspace this browser had selected); a
      // removal made mid-session lands here at the next sign-in, not before.
      return { ...state, tenants: newTenants, activeTenant: null };
    }
    case 'TENANT_RENAMED': {
      // A workspace was renamed from Gestione Workspace. Patches ONLY the name, in the
      // list and in the active workspace. ⚠️ Boot re-reads `tenants` from /auth/me (the
      // token replayed), so the old name returns on reload until the next sign-in —
      // accepted; the UI says so. Not TENANTS_REFRESHED: that one means "a whole new session".
      // ⛔ And it must never be routed through TENANTS_REFRESHED for a second reason:
      // that case REPLACES the list and can drop `activeTenant` (the branch above), i.e.
      // it can eject the user to the picker. A rename is a one-field edit; nothing about
      // membership changed, so no navigation may follow from it.
      const patch = (t) => (t.tenant_id === action.tenant_id ? { ...t, display_name: action.display_name } : t);
      return {
        ...state,
        tenants: (state.tenants || []).map(patch),
        activeTenant: state.activeTenant ? patch(state.activeTenant) : state.activeTenant,
      };
    }
    case 'TOKEN_REFRESHED':
      // A non-login endpoint handed back a re-minted access token — today only
      // the invitation accept (AuthContext.applySession). Only the token changes
      // here; the claims that came with it are applied by the SET_SUPERADMIN /
      // CUSTOMERS_REFRESHED / TENANTS_REFRESHED dispatches beside it, so this
      // case stays deliberately narrow.
      return { ...state, token: action.token };
    case 'CUSTOMERS_REFRESHED':
      // Fed by AuthContext.applyClaims, i.e. only on a whole new session (a
      // sign-in, or an invitation accept). A PB5 grant or revocation made
      // mid-session cannot land here — there is no claims-refresh route.
      // ⛔ Never feed it from /auth/me, which replays the caller's own claims.
      // See the note on `customers` in initialState.
      return { ...state, customers: action.customers || [] };
    default:
      return state;
  }
}

// ── Boot sequence ────────────────────────────────────────────────────────
// The three landings without an active workspace — shared by boot's "no workspaces", "none saved" and "the saved
// one was purged" paths (same payloads as before the extraction).
function bootWithoutActiveTenant(dispatch, { user, token, tenants, customers, isSuperadmin }) {
  const base = { type: 'BOOT_DONE', user, token, customers, activeTenant: null, applications: [] };
  if (tenants.length > 0) {
    dispatch({ ...base, phase: 'tenant_pick', tenants, isSuperadmin, adminMode: false });
    return;
  }
  if (isSuperadmin) {
    // Pure superadmin (or one whose last workspace was purged): land directly in admin mode.
    dispatch({ ...base, phase: 'ready', tenants: [], isSuperadmin: true, adminMode: true });
    return;
  }
  dispatch({ ...base, phase: 'no_tenants', tenants: [], isSuperadmin, adminMode: false });
}

async function bootSequence(dispatch) {
  const { token, tenants, customers, tenantId } = loadAuthStorage();

  if (!token) {
    dispatch({ type: 'BOOT_DONE', phase: 'login', user: null, token: null, tenants: [], customers: [], activeTenant: null, applications: [], adminMode: false });
    return;
  }

  // Validate token with GET /me
  let user;
  try {
    const res = await fetch(`${API_V1}/auth/me`, {
      headers: { Authorization: `Bearer ${token}`, ...getSessionHeaders() },
    });
    if (!res.ok) throw new Error('invalid');
    user = await res.json();
  } catch {
    clearAuthStorage();
    dispatch({ type: 'BOOT_DONE', phase: 'login', user: null, token: null, tenants: [], customers: [], activeTenant: null, applications: [], adminMode: false });
    return;
  }

  // /me replays the token's claims, so a workspace purged since sign-in is still listed: filter the ids this
  // browser has seen answer 410 TENANT_PURGED (auth/tenantPurged.js).
  const purged = loadPurgedTenants();
  const resolvedTenants = withoutPurged(user.tenants || tenants || [], purged);
  // Same precedence as tenants: the /me payload wins, the persisted copy is the
  // offline fallback, `[]` is the old-backend default. Both replay the same JWT
  // claims, so neither is fresher than the other — see initialState.
  const resolvedCustomers = user.customers || customers || [];
  const isSuperadmin = !!user.is_superadmin;
  const landing = { user, token, customers: resolvedCustomers, isSuperadmin };

  // A saved workspace that was purged is forgotten, so boot shows the picker instead of silently falling back
  // to another workspace below (decision: never auto-select after a purge).
  let savedTenantId = tenantId;
  if (savedTenantId && purged.includes(savedTenantId)) {
    localStorage.removeItem('auth_tenant_id');
    savedTenantId = null;
  }

  if (resolvedTenants.length === 0 || !savedTenantId) {
    bootWithoutActiveTenant(dispatch, { ...landing, tenants: resolvedTenants });
    return;
  }

  const activeTenant = resolvedTenants.find(t => t.tenant_id === savedTenantId) || resolvedTenants[0];

  // Load applications for this tenant
  let applications = [];
  try {
    const res = await fetch(`${API_V1}/user-config/tenants/${activeTenant.tenant_id}/applications`, {
      headers: { Authorization: `Bearer ${token}`, ...getSessionHeaders() },
    });
    if (await isTenantPurgedResponse(res)) {
      // Purged since sign-in: drop it and land as if it had never been there.
      dropPurgedTenant(activeTenant.tenant_id);
      const remaining = resolvedTenants.filter(t => t.tenant_id !== activeTenant.tenant_id);
      bootWithoutActiveTenant(dispatch, { ...landing, tenants: remaining });
      return;
    }
    if (res.ok) applications = await res.json();
  } catch { /* non-fatal */ }

  const adminMode = isSuperadmin && !!localStorage.getItem('auth_admin_mode');
  dispatch({ type: 'BOOT_DONE', phase: 'ready', user, token, tenants: resolvedTenants, customers: resolvedCustomers, activeTenant, applications, isSuperadmin, adminMode });
}

// ── Hook ─────────────────────────────────────────────────────────────────
export function useAuthState() {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const booted = useRef(false);

  useEffect(() => {
    if (booted.current) return;
    booted.current = true;
    bootSequence(dispatch);
  }, []);

  return { state, dispatch };
}
